// CustomAudioPlayer — premium mini-player (desktop 3-zone + mobile compact)
import './css/player.css';
import React, { forwardRef, useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMusic, faPlay, faPause, faForward, faBackward, faRandom, faListUl, faXmark, faForwardFast, faPlus, faStepForward, faPen, faLayerGroup } from '@fortawesome/free-solid-svg-icons';

const formatTime = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const CustomAudioPlayer = forwardRef(({
  audioSrc,
  track,
  onEnded,
  onNextTrack,
  onPrevTrack,
  onShuffle,
  shuffleActive = false,
  nextTrack,
  onSkipUpNext,
  onInfoClick,
  onAddToPlaylist,
  onPlayNext,
  onUpdateInfo,
  onSimilar,
  useExternalControls = false,
  externalControls = null,
  chromeVisible = true,
  onPlayingChange,
  activePlaylist = null,
  onClearPlaylist,
}, ref) => {
  const endedFiredRef = useRef(false);
  useEffect(() => { endedFiredRef.current = false; }, [track]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showTrackActions, setShowTrackActions] = useState(false);
  const trackActionsRef = useRef(null);
  const artworkRef = useRef(null);

  useEffect(() => {
    if (!showTrackActions) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!trackActionsRef.current?.contains(event.target) && !artworkRef.current?.contains(event.target)) {
        setShowTrackActions(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setShowTrackActions(false);
    };
    document.addEventListener('pointerdown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showTrackActions]);

  useEffect(() => {
    setShowTrackActions(false);
  }, [track?.video_id]);

  useEffect(() => {
    onPlayingChange?.(isPlaying);
  }, [isPlaying, onPlayingChange]);

  useEffect(() => {
    if (!useExternalControls || !externalControls) return;
    const poll = () => {
      try {
        const cur = externalControls.getTime ? externalControls.getTime() : 0;
        const dur = externalControls.getDuration ? externalControls.getDuration() : 0;
        const playing = externalControls.isPlaying ? externalControls.isPlaying() : false;
        setCurrentTime(cur || 0);
        setDuration(dur || 0);
        setIsPlaying(!!playing);
      } catch (e) {}

      if (externalControls && typeof onEnded === 'function') {
        try {
          const d = externalControls.getDuration ? externalControls.getDuration() : 0;
          const c = externalControls.getTime ? externalControls.getTime() : 0;
          if (d > 0 && c >= d - 0.75 && !endedFiredRef.current) {
            endedFiredRef.current = true;
            onEnded();
          }
          if (c < (d - 2)) endedFiredRef.current = false;
        } catch (e) {}
      }
    };
    const id = setInterval(poll, 500);
    return () => clearInterval(id);
  }, [useExternalControls, externalControls, onEnded]);

  useEffect(() => {
    if (useExternalControls && externalControls) return;
    const audio = ref.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration || 0);
    const syncPause = () => setIsPlaying(false);
    const syncPlay = () => setIsPlaying(true);

    const handleEnded = () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none';
        navigator.mediaSession.playbackState = 'playing';
      }
      setIsPlaying(false);
      try { onEnded(); } catch (err) {
        console.warn('onEnded failed:', err);
      }
    };

    const watchdog = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (audio.paused && audio.src && audio.readyState > 0 && !audio.ended) {
        const nearStart = Math.abs(audio.currentTime) < 0.1;
        const nearEnd = Math.abs(audio.currentTime - audio.duration) < 1;
        if (nearStart || nearEnd) {
          audio.play().catch(err => {
            if (err.name !== 'NotSupportedError') {
              console.warn('Watchdog resume failed:', err);
            }
          });
        }
      }
    }, 3000);

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('pause', syncPause);
    audio.addEventListener('play', syncPlay);
    audio.addEventListener('ended', handleEnded);

    return () => {
      clearInterval(watchdog);
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('pause', syncPause);
      audio.removeEventListener('play', syncPlay);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [ref, onEnded, useExternalControls, externalControls]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !track) return;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: track.title,
      artist: track.artist,
      album: track.artist,
      artwork: [
        { src: track.thumbnail_default, sizes: '96x96', type: 'image/jpeg' },
        { src: track.thumbnail_default, sizes: '128x128', type: 'image/jpeg' },
        { src: track.thumbnail_default, sizes: '192x192', type: 'image/jpeg' },
        { src: track.thumbnail_default, sizes: '512x512', type: 'image/jpeg' },
      ]
    });

    navigator.mediaSession.setActionHandler('play', () => (
      useExternalControls && externalControls?.play
        ? externalControls.play()
        : ref.current?.play()
    ));
    navigator.mediaSession.setActionHandler('pause', () => (
      useExternalControls && externalControls?.pause
        ? externalControls.pause()
        : ref.current?.pause()
    ));
    navigator.mediaSession.setActionHandler('previoustrack', () => onPrevTrack?.());
    navigator.mediaSession.setActionHandler('nexttrack', () => onNextTrack?.());
  }, [track, ref, onNextTrack, onPrevTrack, useExternalControls, externalControls]);

  const togglePlay = () => {
    if (useExternalControls && externalControls) {
      try {
        if (externalControls.isPlaying?.()) externalControls.pause?.();
        else externalControls.play?.();
      } catch (e) {}
      return;
    }
    const audio = ref.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {
        alert('Unable to play this track. Please try again.');
      });
    } else {
      audio.pause();
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (useExternalControls && externalControls?.seek) {
      try { externalControls.seek(newTime); } catch (err) {}
      setCurrentTime(newTime);
      return;
    }
    const audio = ref.current;
    if (!audio) return;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleInfoActivate = () => {
    if (onInfoClick) {
      requestAnimationFrame(() => {
        onInfoClick({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const progress = Math.max(0, Math.min(100, (currentTime / (duration || 1)) * 100));
  const title = track?.title || 'Not playing';
  const artist = track?.artist || 'Select a track from your library';
  const nextLabel = nextTrack
    ? `Up next · ${nextTrack.title}${nextTrack.artist ? ` — ${nextTrack.artist}` : ''}`
    : null;

  return (
    <div
      className={`audio-player-sticky${chromeVisible ? '' : ' audio-player-sticky--hidden'}`}
      aria-hidden={!chromeVisible}
    >
      <div
        className="audio-player-progress-mobile"
        style={{ '--p': `${progress}%` }}
        aria-hidden="true"
      />

      <div className="audio-player-content">
        <div className="audio-player-left">
          <button
            ref={artworkRef}
            type="button"
            className="audio-player-art"
            onClick={() => setShowTrackActions((open) => !open)}
            aria-label="Now playing options"
            aria-expanded={showTrackActions}
          >
            <div className="audio-player-art-fallback">
              <FontAwesomeIcon icon={faMusic} />
            </div>
            {(track?.thumbnail_medium || track?.thumbnail_default) && (
              <img
                key={track?.video_id || track?.id}
                src={track.thumbnail_medium || track.thumbnail_default}
                alt=""
              />
            )}
          </button>

          <div
            className="audio-player-info"
            role="button"
            tabIndex={0}
            onClick={handleInfoActivate}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleInfoActivate()}
          >
            <div className="audio-player-title">{title}</div>
            <div className="audio-player-artist">{artist}</div>
            {nextLabel && activePlaylist && (
              <div className="audio-player-next">{nextLabel}</div>
            )}
          </div>
        </div>

        <div className="audio-player-center">
          <div className="control-buttons">
            <button type="button" className="control-btn" onClick={onPrevTrack} aria-label="Previous">
              <FontAwesomeIcon icon={faBackward} />
            </button>
            <button
              type="button"
              className="control-btn primary"
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </button>
            <button type="button" className="control-btn" onClick={onNextTrack} aria-label="Next">
              <FontAwesomeIcon icon={faForward} />
            </button>
            <button
              type="button"
              className={`control-btn${shuffleActive ? ' active' : ''}`}
              onClick={onShuffle}
              aria-label="Shuffle"
              aria-pressed={shuffleActive}
              title={shuffleActive ? 'Shuffle on' : 'Shuffle off'}
            >
              <FontAwesomeIcon icon={faRandom} />
            </button>
          </div>

          <div className="audio-player-seek">
            <span className="audio-player-time">{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              style={{ '--p': `${progress}%` }}
              aria-label="Seek"
            />
            <span className="audio-player-time">{formatTime(duration)}</span>
          </div>
        </div>

        <div className="audio-player-right">
          {activePlaylist ? (
            <div className="audio-player-playlist">
              <div
                className={`audio-player-playlist-art${activePlaylist.isPersona ? ' is-persona' : ''}`}
                aria-hidden="true"
              >
                {activePlaylist.isPersona ? (
                  <span>{activePlaylist.emoji || '🎧'}</span>
                ) : activePlaylist.coverUrl ? (
                  <img src={activePlaylist.coverUrl} alt="" />
                ) : (
                  <FontAwesomeIcon icon={faListUl} />
                )}
              </div>
              <div className="audio-player-playlist-meta">
                <div className="audio-player-playlist-label">
                  {activePlaylist.label
                    || (activePlaylist.isSearch ? 'Search' : 'Playlist')}
                </div>
                <div className="audio-player-playlist-title">
                  {activePlaylist.name}
                  {activePlaylist.extraSources > 0 && (
                    <span className="audio-player-playlist-extra">
                      {' '}+{activePlaylist.extraSources}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                className="audio-player-playlist-close"
                onClick={onClearPlaylist}
                aria-label={
                  activePlaylist.isPersona
                    ? 'Stop personality play'
                    : activePlaylist.isBrowse
                      ? `Remove ${activePlaylist.label || 'filter'} from queue`
                      : activePlaylist.isSearch ? 'Remove search from queue' : 'Remove from queue'
                }
                title={
                  activePlaylist.isPersona
                    ? 'Back to regular play'
                    : activePlaylist.isBrowse
                      ? 'Remove from queue'
                      : activePlaylist.isSearch ? 'Remove search from queue' : 'Remove from queue'
                }
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          ) : nextTrack ? (
            <div className="audio-player-upnext">
              <div className="audio-player-upnext-art" aria-hidden="true">
                {(nextTrack.thumbnail_medium || nextTrack.thumbnail_default) ? (
                  <img
                    src={nextTrack.thumbnail_medium || nextTrack.thumbnail_default}
                    alt=""
                  />
                ) : (
                  <FontAwesomeIcon icon={faMusic} />
                )}
              </div>
              <div className="audio-player-upnext-meta">
                <div className="audio-player-upnext-label">Up next</div>
                <div className="audio-player-upnext-title">{nextTrack.title}</div>
                {nextTrack.artist && (
                  <div className="audio-player-upnext-artist">{nextTrack.artist}</div>
                )}
              </div>
              <button
                type="button"
                className="audio-player-upnext-skip"
                onClick={onSkipUpNext}
                aria-label="Skip up next"
                title="Skip up next"
              >
                <FontAwesomeIcon icon={faForwardFast} />
              </button>
            </div>
          ) : null}
          <div className="audio-player-mobile-controls">
            <button
              type="button"
              className="control-btn primary"
              onClick={togglePlay}
              aria-label={isPlaying ? 'Pause' : 'Play'}
            >
              <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </button>
            <button type="button" className="control-btn" onClick={onNextTrack} aria-label="Next">
              <FontAwesomeIcon icon={faForward} />
            </button>
          </div>
        </div>
      </div>

      {showTrackActions && track && (
        <div ref={trackActionsRef} className="audio-player-track-actions" role="group" aria-label="Now playing actions">
          <div className="audio-player-track-actions-title">{title}</div>
          <div className="audio-player-track-actions-grid">
            <button type="button" onClick={() => { setShowTrackActions(false); onAddToPlaylist?.(); }}>
              <FontAwesomeIcon icon={faPlus} /> Add to Playlist
            </button>
            <button type="button" onClick={() => { setShowTrackActions(false); onPlayNext?.(); }}>
              <FontAwesomeIcon icon={faStepForward} /> Play Next
            </button>
            <button type="button" onClick={() => { setShowTrackActions(false); onUpdateInfo?.(); }}>
              <FontAwesomeIcon icon={faPen} /> Update Info
            </button>
            <button type="button" onClick={() => { setShowTrackActions(false); onSimilar?.(track); }}>
              <FontAwesomeIcon icon={faLayerGroup} /> Similar
            </button>
          </div>
        </div>
      )}

      <audio ref={ref} src={audioSrc} preload="auto" />
    </div>
  );
});

export default CustomAudioPlayer;
